// Lazily-created pool of terrain compute workers. Spreads tile computation
// across a few cores while keeping the main thread free for map interaction.
// Round-robin dispatch with id-correlated request/response promises.

const POOL_SIZE = Math.max(2, Math.min(4, (navigator.hardwareConcurrency || 4) - 1))

let workers = null
let next = 0
let seq = 0
const pending = new Map() // id -> { resolve, reject }

function rejectAll(reason) {
  for (const { reject } of pending.values()) reject(reason)
  pending.clear()
}

function ensurePool() {
  if (workers) return
  workers = []
  for (let i = 0; i < POOL_SIZE; i++) {
    const w = new Worker(new URL('./terrain-worker.js', import.meta.url))
    w.onmessage = (e) => {
      const { id, data, error } = e.data
      const entry = pending.get(id)
      if (!entry) return // superseded/aborted
      pending.delete(id)
      if (error) entry.reject(new Error(error))
      else entry.resolve(data)
    }
    w.onerror = (err) => {
      // An uncaught worker error leaves its in-flight requests unanswered;
      // fail them all and rebuild the pool on next use rather than hang tiles.
      console.error('terrain-worker:', err.message || err)
      rejectAll(new Error('terrain worker crashed'))
      workers = null
    }
    workers.push(w)
  }
}

// Compute one tile. `elev` is structure-cloned (not transferred) so the shared
// elevation cache copy on the main thread stays intact. Resolves to a PNG
// ArrayBuffer ready to return from the maptiler protocol handler.
export function computeTileInWorker(elev, z, x, y, filters, viewshed, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(new DOMException('Aborted', 'AbortError'))
      return
    }
    ensurePool()
    const id = ++seq
    const worker = workers[next]
    next = (next + 1) % workers.length

    pending.set(id, { resolve, reject })

    if (signal) {
      signal.addEventListener('abort', () => {
        if (pending.delete(id)) reject(new DOMException('Aborted', 'AbortError'))
      }, { once: true })
    }

    worker.postMessage({ id, elev, z, x, y, filters, viewshed })
  })
}
